import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, ShieldCheck, Landmark, Mail, Phone, Loader2, Building } from "lucide-react";
import apiClient from "@/services/apiClient";
import { motion } from "framer-motion";
import { useToast } from "@/contexts/ToastContext";

interface TumaStatus {
    is_active: boolean;
    business_email?: string;
    updated_at?: string;
}

export const TumaSetup: React.FC = () => {
    const navigate = useNavigate();
    const { warning, success, error: toastError } = useToast();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<TumaStatus | null>(null);

    // Form fields
    const [tumaBusinessName, setTumaBusinessName] = useState("");
    const [tumaEmail, setTumaEmail] = useState("");
    const [tumaPhoneNumber, setTumaPhoneNumber] = useState("");
    const [tumaAccountNumber, setTumaAccountNumber] = useState("");
    
    // Bank Selection
    const [tumaBankId, setTumaBankId] = useState<string>("");
    const [tumaBanks, setTumaBanks] = useState<Array<{ id: number; name: string }>>([]);
    const [fetchingBanks, setFetchingBanks] = useState(false);

    useEffect(() => {
        fetchStatus();
        fetchTumaBanks();
    }, []);

    const fetchStatus = async () => {
        try {
            const res = await apiClient.get("/landlord/tuma/status");
            setStatus(res.data);
        } catch (e) {
            console.log("Failed to fetch Tuma status", e);
        }
    };

    const fetchTumaBanks = async () => {
        setFetchingBanks(true);
        try {
            const res = await apiClient.get("/tuma/banks");
            if (res.data?.success && Array.isArray(res.data.data)) {
                setTumaBanks(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch Tuma banks", error);
        } finally {
            setFetchingBanks(false);
        }
    };

    const saveTumaCredentials = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!tumaBusinessName || !tumaEmail || !tumaPhoneNumber || !tumaBankId || !tumaAccountNumber) {
            warning('Incomplete form', 'Please fill all required Tuma onboarding fields.');
            return;
        }

        setLoading(true);
        try {
            await apiClient.post(`/landlord/tuma/credentials`, {
                name: tumaBusinessName.trim(),
                email: tumaEmail.trim(),
                mobile: tumaPhoneNumber.trim(),
                bankId: tumaBankId,
                accountNumber: tumaAccountNumber.trim(),
            });

            success('Tuma connected!', 'Your property is now connected to Tuma M-Pesa. Tenants can pay invoices seamlessly!');
            navigate(-1);
        } catch (error: any) {
            console.error("Tuma Onboarding error:", error.response?.data);
            toastError('Connection failed', error.response?.data?.error || 'Failed to automate gateway. Please check your bank details.');
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "w-full pl-12 pr-4 py-3 border border-light-border dark:border-dark-border rounded-xl bg-light-input dark:bg-dark-input focus:ring-2 focus:ring-primary/50 outline-none text-light-text dark:text-dark-text font-medium transition-colors";
    const labelClass = "block text-sm font-bold text-light-text dark:text-dark-text mb-2";

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-24">
            <div className="flex items-center justify-between bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors rounded-full"
                    >
                        <ArrowLeft size={24} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                            Automate Payments (Tuma)
                        </h1>
                        <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle mt-1">
                            Link your property to a secure M-Pesa integration
                        </p>
                    </div>
                </div>
            </div>

            {status?.is_active && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-green-50 dark:bg-green-900/10 p-6 rounded-2xl border border-green-200 dark:border-green-900/30 flex items-start gap-4">
                    <CheckCircle className="text-green-600 dark:text-green-500 mt-1 flex-shrink-0" size={28} />
                    <div>
                        <h2 className="text-lg font-bold text-green-800 dark:text-green-400 mb-1">Tuma Integration Active</h2>
                        <p className="text-sm font-medium text-green-700 dark:text-green-500/80 leading-relaxed">
                            Your property is fully connected to Tuma via {status.business_email}. Tenants can now pay invoices via in-app STK push, and their balances will update automatically!
                        </p>
                    </div>
                </motion.div>
            )}

            <div className="bg-light-card dark:bg-dark-card rounded-2xl border border-light-border dark:border-dark-border shadow-sm overflow-hidden">
                <div className="p-8 border-b border-light-border dark:border-dark-border bg-primary/5">
                    <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-2 flex items-center gap-2">
                        <Landmark className="text-primary" size={24} /> Connect Settlement Bank
                    </h2>
                    <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle max-w-2xl leading-relaxed">
                        Provide your business and bank details. We will automatically generate a secure M-Pesa integration powered by Tuma. Funds collected from tenants will settle directly into this bank account.
                    </p>
                </div>

                <form onSubmit={saveTumaCredentials} className="p-8 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Column 1 */}
                        <div className="space-y-6">
                            <div>
                                <label className={labelClass}>Business / Property Name</label>
                                <div className="relative">
                                    <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Majengo Heights"
                                        value={tumaBusinessName}
                                        onChange={(e) => setTumaBusinessName(e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Contact Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                    <input
                                        type="email"
                                        required
                                        placeholder="property@example.com"
                                        value={tumaEmail}
                                        onChange={(e) => setTumaEmail(e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Contact Phone Number</label>
                                <div className="relative">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                    <input
                                        type="tel"
                                        required
                                        placeholder="07XX XXX XXX"
                                        value={tumaPhoneNumber}
                                        onChange={(e) => setTumaPhoneNumber(e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Column 2 */}
                        <div className="space-y-6">
                            <div>
                                <label className={labelClass}>Settlement Bank</label>
                                <div className="relative">
                                    <Landmark className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                    <select
                                        required
                                        value={tumaBankId}
                                        onChange={(e) => setTumaBankId(e.target.value)}
                                        className={`w-full pl-12 pr-4 py-3 border border-light-border dark:border-dark-border rounded-xl bg-light-input dark:bg-dark-input focus:ring-2 focus:ring-primary/50 outline-none font-medium transition-colors ${tumaBankId ? 'text-light-text dark:text-dark-text' : 'text-light-subtle dark:text-dark-subtle'}`}
                                    >
                                        <option value="" disabled>{fetchingBanks ? 'Loading banks...' : 'Select your bank...'}</option>
                                        {tumaBanks.map(bank => (
                                            <option key={bank.id} value={bank.id}>{bank.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Bank Account Number</label>
                                <div className="relative">
                                    <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                    <input
                                        type="number"
                                        required
                                        placeholder="e.g. 1122334455"
                                        value={tumaAccountNumber}
                                        onChange={(e) => setTumaAccountNumber(e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-light-border dark:border-dark-border flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center justify-center gap-2 bg-primary text-white font-bold px-8 py-4 rounded-xl shadow-md hover:bg-primary/90 transition-all disabled:opacity-50 min-w-[250px]"
                        >
                            {loading ? (
                                <><Loader2 className="animate-spin" size={20} /> Connecting...</>
                            ) : status?.is_active ? (
                                "Update Tuma Connection"
                            ) : (
                                "Automate M-Pesa with Tuma"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
